import React, { useState, useEffect, useContext } from "react";
import { API_URL } from "../components/api";
import Modal from "react-bootstrap/Modal";

import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridFooterContainer,
  GridPagination,
} from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";

import ExcelSVG from "../../ExcelSVG.svg";
import FilterSVG from "../../FilterSVG.svg";
import Button from "react-bootstrap/Button";
import * as XLSX from "xlsx";
import AuthContext from "../context/AuthContext";
import EditONU from "./EditONU";

// For API Requests
import axios from "axios";

function ONUTables() {
  // State hooks for managing data and UI states
  const [data, setData] = useState([]); // Stores the data fetched from the API
  const [searchQuery, setSearchQuery] = useState(""); // Tracks the search query entered by the user
  const [loading, setLoading] = useState(true); // Determines if data is still loading
  const [show, setShow] = useState(false); // Manages modal visibility for details
  const [showFullDetails, setShowFullDetails] = useState(false); // Manages modal visibility for full details
  const { user } = useContext(AuthContext); // Accessing the current user context
  const authTokens = JSON.parse(localStorage.getItem("authTokens")); // Fetching auth token from localStorage
  const handleshowFullDetails = () => setShowFullDetails(true);
  const handlecloseFullDetails = () => setShowFullDetails(false);

  // Full details state for storing the currently selected ONU's data
  const [fullDetails, setFullDetails] = useState({
    onu_id: 0,
    onu_name: "",
    mac_address: "",
    pon: "",
    olt_id: 0,
    olt_name: "",
    core_id: 0,
    zone_name: "",
    code: "",
  });

  // Updates the full details for a selected ONU
  const updateFullDetails = (fullDetails) => {
    setFullDetails(fullDetails);
  };
  const [filters, setFilters] = useState({
    onu_name: "",
    mac_address: "",
    pon: "",
    olt_name: "",
    code: "",
    zone_name: "",
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({
    items: [],
  });

  const [searchInput, setSearchInput] = useState(""); // To capture search input

  // NEW: State to track column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [searchFilterTimeout, setSearchFilterTimeout] = useState(null);

  // Fetch data from the API
  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/radius/fiber/cores/onu/`, {
        headers: {
          Authorization: `Bearer ${authTokens.access}`, // Authorization token in the header
        },
        params: {
          username: user.username, // Include the username in the query params
        },
      });

      // If the response is successful, reverse the data and set it
      if (response.status === 200) {
        setData(response.data.reverse());
        console.log(response.data.reverse()); // Log the data for debugging
      }
    } catch (error) {
      console.error(error); // Log any errors during the API call
    } finally {
      setLoading(false); // Set loading to false after the data is fetched
    }
  };

  // Fetch data when component mounts or user/authorization changes
  useEffect(() => {
    fetchData();
  }, [user.username, authTokens.access]);

  // Defines the columns for the DataGrid table
  const columns = [
    { field: "onu_name", headerName: "ONU Name", flex: 2, minWidth: 150 },
    { field: "mac_address", headerName: "Mac Address", flex: 2, minWidth: 150 },
    {
      field: "pon",
      headerName: "PON",
      minWidth: 230,
    },
    { field: "olt_name", headerName: "OLT Name", flex: 2, minWidth: 150 },
    { field: "code", headerName: "Code", flex: 2, minWidth: 150 },
    { field: "zone_name", headerName: "Zone Name", flex: 2, minWidth: 150 },
    {
      field: "signal_strength",
      headerName: "Signal",
      minWidth: 230,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 230,
      flex: 2,
      renderCell: (params) => (
        <div>
          <button
            className="edit-client"
            onClick={() => {
              // Set full details for the selected ONU and show full details modal
              const fullDetails = {
                onu_id: params.row.onu_id,
                onu_name: params.row.onu_name,
                mac_address: params.row.mac_address,
                pon: params.row.pon,
                olt_id: params.row.olt_id,
                olt_name: params.row.olt_name,
                core_id: params.row.core_id,
                code: params.row.code,
              };
              updateFullDetails(fullDetails);
              handleshowFullDetails();
            }}
          >
            Edit
          </button>
        </div>
      ),
    },
  ];

  // Define the row ID for the DataGrid
  const getRowId = (row) => row.onu_id;

  // Create a delayed search filter function
  const delaySearchFilter = (input) => {
    setSearchQuery(input);
  };

  // Effect to handle session storage for search query
  useEffect(() => {
    const searchValue = sessionStorage.getItem("searchONU"); // Get saved search value from session storage
    Object.keys(sessionStorage).forEach((key) => {
      if (key !== "searchONU") {
        sessionStorage.removeItem(key); // Clear other session data but keep search query
      }
    });

    if (searchValue) {
      sessionStorage.setItem("searchONU", searchValue);
      setSearchQuery(searchValue); // Set the search query if available
    }
  }, []);

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchInput(query);
    sessionStorage.setItem("searchONU", query);

    if (searchFilterTimeout) {
      clearTimeout(searchFilterTimeout);
    }

    const newTimeout = setTimeout(() => {
      delaySearchFilter(query);
    }, 100);
    setSearchFilterTimeout(newTimeout);
  };

  // IMPROVED: Combined filtering logic that works for both display AND export
  const filteredRows = data.filter((row) => {
    // Apply search query filter
    const searchMatch = searchQuery
      .split(" ")
      .every((word) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(word.toLowerCase().trim())
        )
      );

    // Apply modal filters
    const modalFilterMatch = Object.keys(filters).every((key) => {
      if (!filters[key]) return true;

      if (key === "connectivity_dependent") {
        const filterValue = filters[key].toLowerCase();
        if (row.connectivity === "Fiber") {
          return (row.code || "").toLowerCase().includes(filterValue);
        } else if (row.connectivity === "Wireless") {
          return (row.name || "").toLowerCase().includes(filterValue);
        }
        return false;
      }

      const rowValue = String(row[key] || "").toLowerCase();
      const filterValue = String(filters[key]).toLowerCase();
      return rowValue.includes(filterValue);
    });

    // Apply DataGrid filters
    const gridFilterMatch = gridFilterModel.items.every((filterItem) => {
      if (!filterItem.value) return true;

      const cellValue = row[filterItem.field];
      const filterValue = filterItem.value;

      switch (filterItem.operator) {
        case "contains":
          return String(cellValue || "")
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        case "equals":
          return String(cellValue || "") === String(filterValue);
        case "startsWith":
          return String(cellValue || "")
            .toLowerCase()
            .startsWith(String(filterValue).toLowerCase());
        case "endsWith":
          return String(cellValue || "")
            .toLowerCase()
            .endsWith(String(filterValue).toLowerCase());
        default:
          return true;
      }
    });

    return searchMatch && modalFilterMatch && gridFilterMatch;
  });

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  // Column headers for export
  const columnHeaders = {
    onu_name: "Name",
    mac_address: "Mac Address",
    pon: "PON",
    olt_name: "OLT Name",
    code: "Code",
    signal_strength: "Signal Strength",
    zone_name: "Zone Name",
  };

  // Format date fields properly for export — e.g. "21/02/2025 10:25:52 AM"
  const formatDateForExport = (dateValue) => {
    if (!dateValue) return "";
    const date = new Date(dateValue);

    // Format as DD/MM/YYYY HH:MM:SS AM/PM
    const formattedDate = date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    // Ensure consistent spacing before AM/PM
    return formattedDate.replace(" am", " AM").replace(" pm", " PM");
  };

  /// Corrected: Transform rows respecting column visibility
  const transformRowsForExport = (rows) => {
    return rows.map((row) => {
      const transformedRow = {};

      Object.keys(columnHeaders).forEach((field) => {
        // Only export if column is visible
        if (columnVisibilityModel[field] !== false) {
          const headerName = columnHeaders[field];

          // Default: use the value or empty string
          transformedRow[headerName] = row[field] ?? "";
        }
      });

      return transformedRow;
    });
  };

  // IMPROVED: Export function that uses the already-filtered rows
  const handleExportToExcel = () => {
    console.log("Exporting data...");
    console.log("Total data rows:", data.length);
    console.log("Filtered rows:", filteredRows.length);
    console.log("Active filters:", {
      search: searchQuery,
      modalFilters: filters,
      gridFilters: gridFilterModel.items,
    });
    console.log("Column visibility:", columnVisibilityModel);

    // Always use filteredRows which has ALL filters applied
    const transformedRows = transformRowsForExport(filteredRows);

    console.log("Rows to export:", transformedRows.length);

    const worksheet = XLSX.utils.json_to_sheet(transformedRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ONU"); // Add sheet to workbook
    XLSX.writeFile(workbook, "ONU.xlsx"); // Trigger download of the Excel file
  };

  const [isPressed, setIsPressed] = useState(false);

  const handleExportClick = () => {
    setIsPressed(true);
    handleExportToExcel();
    setTimeout(() => {
      setIsPressed(false);
    }, 200);
  };

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <Button
        onClick={() => setShowFilterModal(true)}
        variant="outline-primary"
        style={{
          border: "none",
        }}
      >
        <img
          src={FilterSVG}
          alt="Filter Icon"
          style={{
            color: "white",
            width: "18px",
            marginRight: "8px",
            filter: "invert(0)", // keep black icon
          }}
        />
        Filter
      </Button>
      {/*<GridToolbarFilterButton />*/}
      <GridToolbarExport />
      <Button
        onClick={handleExportClick}
        variant="outline-success"
        style={{
          display: "flex",
          alignItems: "center",
          padding: "5px 10px",
          fontSize: "14px",
          border: "none",
          backgroundColor: isPressed ? "white" : "transparent",
          color: isPressed ? "black" : "#007bff",
          cursor: "pointer",
        }}
      >
        <img
          src={ExcelSVG}
          alt="Excel Icon"
          style={{
            width: "20px",
            marginRight: "8px",
            filter: isPressed ? "invert(0)" : "invert(1)",
          }}
        />
        Excel
      </Button>
    </GridToolbarContainer>
  );

  // Custom footer for the DataGrid (pagination controls)
  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          padding: "10px",
        }}
      >
        <GridPagination />
      </div>
    </GridFooterContainer>
  );

  return (
    <div className="ClientTable">
      {/* Search input for filtering rows */}
      <style>{`
        .MuiDataGrid-panelWrapper {
          background: #122632 !important;
        }
      `}</style>
      <input
        type="text"
        placeholder="Search..."
        value={searchInput}
        onChange={handleSearchChange}
        className="search-client"
      />
      {!loading && data.length > 0 && (
        <div style={{ height: 520, width: "100%" }}>
          {/* DataGrid component for displaying rows */}
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={getRowId}
            filterModel={gridFilterModel}
            onFilterModelChange={(newModel) => setGridFilterModel(newModel)}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            components={{
              Footer: () => <CustomFooter />,
              Toolbar: CustomToolbar,
            }}
            rowsPerPageOptions={[10, 100, 1000]}
            pagination
            disableSelectionOnClick
          />
        </div>
      )}
      {/* Modal for displaying full details */}
      <Modal
        show={showFullDetails}
        onHide={handlecloseFullDetails}
        className="modal-lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Building</Modal.Title>
        </Modal.Header>
        <div className="Subform">
          <EditONU
            onu_id={fullDetails.onu_id}
            onu_name={fullDetails.onu_name}
            mac_address={fullDetails.mac_address}
            pon={fullDetails.pon}
            olt_id={fullDetails.olt_id}
            olt_name={fullDetails.olt_name}
            core_id={fullDetails.core_id}
            code={fullDetails.code}
          />
        </div>
      </Modal>
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Filters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {columns
            .filter((col) => col.field !== "actions")
            .map((col) => (
              <div key={col.field} className="filter">
                <label>{col.headerName}</label>
                <input
                  type="text"
                  placeholder={`Filter by ${col.headerName}`}
                  value={filters[col.field] || ""}
                  onChange={(e) =>
                    handleFilterChange(col.field, e.target.value)
                  }
                  className="filter-input"
                />
              </div>
            ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFilterModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ONUTables;
